import React, { useEffect, useRef, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { io, Socket } from 'socket.io-client';
import '@xterm/xterm/css/xterm.css';

interface XTermDebugConsoleProps {
  sshConfig: {
    host: string;
    username: string;
    password: string;
  };
  sessionId: string;
  onOutput?: (data: string) => void;
}

const XTermDebugConsole: React.FC<XTermDebugConsoleProps> = ({ 
  sshConfig, 
  sessionId,
  onOutput 
}) => {
  const terminalRef = useRef<HTMLDivElement>(null);
  const [terminal, setTerminal] = useState<Terminal | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const hasConnectedRef = useRef(false);
  const terminalInstanceRef = useRef<Terminal | null>(null);
  const socketInstanceRef = useRef<Socket | null>(null);

  useEffect(() => {
    console.log('XTermDebugConsole useEffect:', {
      hasRef: !!terminalRef.current,
      sshConfig,
      sessionId,
      hasConnected: hasConnectedRef.current,
      hasTerminal: !!terminalInstanceRef.current,
      hasSocket: !!socketInstanceRef.current
    });
    
    if (!terminalRef.current || !sshConfig || !sessionId) return;
    
    // Check if we already have instances - if so, don't create new ones
    if (terminalInstanceRef.current && socketInstanceRef.current) {
      console.log('Already have terminal and socket instances, skipping initialization');
      return;
    }
    
    // Prevent multiple connections
    let mounted = true;
    let socketInstance: Socket | null = null;

    // Initialize xterm.js
    const term = new Terminal({
      theme: {
        background: '#000000',
        foreground: '#00ff00',
        cursor: '#00ff00',
        cursorAccent: '#000000',
        selectionBackground: 'rgba(0, 255, 0, 0.3)',
        black: '#000000',
        red: '#ff5555',
        green: '#00ff00',
        yellow: '#ffff55',
        blue: '#5555ff',
        magenta: '#ff55ff',
        cyan: '#55ffff',
        white: '#bbbbbb',
        brightBlack: '#555555',
        brightRed: '#ff5555',
        brightGreen: '#55ff55',
        brightYellow: '#ffff55',
        brightBlue: '#5555ff',
        brightMagenta: '#ff55ff',
        brightCyan: '#55ffff',
        brightWhite: '#ffffff'
      },
      fontFamily: 'Consolas, "Courier New", monospace',
      fontSize: 14,
      cursorBlink: true,
      convertEol: true
    });

    // Add addons
    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();
    
    // Delay terminal initialization to ensure DOM is ready
    setTimeout(() => {
      if (!mounted || !terminalRef.current) {
        console.error('Terminal ref not available after timeout');
        return;
      }
      
      try {
        term.loadAddon(fitAddon);
        term.loadAddon(webLinksAddon);
        
        term.open(terminalRef.current);
        fitAddon.fit();
        
        fitAddonRef.current = fitAddon;
        terminalInstanceRef.current = term;
        setTerminal(term);
        
        console.log('Terminal initialized successfully');
      } catch (error) {
        console.error('Failed to initialize terminal:', error);
        return;
      }
    }, 100);

    // Connect to Socket.IO server
    socketInstance = io('https://proxyomega.com', {
      path: '/socket.io/',
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: false, // Disable auto-reconnection
      reconnectionAttempts: 0,
      timeout: 10000
    });

    socketInstance.on('connect', () => {
      if (!mounted || !socketInstance) return;
      console.log('Connected to terminal server');
      
      // Authenticate with session ID
      socketInstance.emit('auth', { sessionId });
    });

    socketInstance.on('authenticated', () => {
      if (!mounted || !socketInstance) return;
      // Connect to SSH
      socketInstance.emit('ssh-connect', sshConfig);
    });

    socketInstance.on('connected', ({ message, cols, rows }) => {
      if (!mounted) return;
      console.log('SSH connected:', message);
      setIsConnected(true);
      hasConnectedRef.current = true;
      term.write('\r\n\x1b[32m' + message + '\x1b[0m\r\n');
    });

    socketInstance.on('data', (data: string) => {
      if (!mounted) return;
      term.write(data);
      // Send output to parent component for parsing
      if (onOutput) {
        onOutput(data);
      }
    });

    socketInstance.on('error', (error: string) => {
      if (!mounted || !socketInstance) return;
      console.error('Socket error:', error);
      term.write(`\r\n\x1b[31mError: ${error}\x1b[0m\r\n`);
      setIsConnected(false);
      // Don't reconnect on error
      socketInstance.disconnect();
    });

    socketInstance.on('exit', ({ exitCode }) => {
      if (!mounted || !socketInstance) return;
      console.log('SSH exit code:', exitCode);
      if (exitCode === 255) {
        term.write(`\r\n\x1b[31mSSH connection failed. Check credentials.\x1b[0m\r\n`);
      } else {
        term.write(`\r\n\x1b[33mConnection closed (exit code: ${exitCode})\x1b[0m\r\n`);
      }
      setIsConnected(false);
      // Disconnect socket on SSH failure
      socketInstance.disconnect();
    });

    socketInstance.on('disconnect', (reason) => {
      if (!mounted || !socketInstance) return;
      console.log('Disconnected from terminal server:', reason);
      
      // Only set disconnected if it's not a ping timeout
      if (reason !== 'ping timeout' && reason !== 'transport close') {
        setIsConnected(false);
      }
      
      // Prevent any reconnection attempts
      if (socketInstance.io) {
        socketInstance.io.opts.reconnection = false;
        socketInstance.io.opts.reconnectionAttempts = 0;
      }
    });

    // Handle terminal input after initialization
    setTimeout(() => {
      if (!mounted || !term) return;
      
      term.onData((data) => {
        if (mounted && socketInstance && socketInstance.connected && isConnected) {
          socketInstance.emit('data', data);
        }
      });
    }, 200);

    if (mounted) {
      socketInstanceRef.current = socketInstance;
      setSocket(socketInstance);
    }

    // Handle window resize
    const handleResize = () => {
      if (fitAddonRef.current) {
        fitAddonRef.current.fit();
        if (socketInstance && socketInstance.connected && isConnected) {
          const { cols, rows } = term;
          socketInstance.emit('resize', { cols, rows });
        }
      }
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      mounted = false;
      hasConnectedRef.current = false;
      window.removeEventListener('resize', handleResize);
      if (socketInstanceRef.current) {
        socketInstanceRef.current.io.opts.reconnection = false;
        socketInstanceRef.current.disconnect();
        socketInstanceRef.current = null;
      }
      if (terminalInstanceRef.current) {
        terminalInstanceRef.current.dispose();
        terminalInstanceRef.current = null;
      }
      setSocket(null);
      setTerminal(null);
    };
  }, [sshConfig, sessionId]);

  // Debug logging for render
  console.log('XTermDebugConsole render:', {
    sshConfig,
    sessionId,
    isConnected,
    hasTerminal: !!terminal
  });

  return (
    <div className="h-full flex flex-col bg-black">
      {/* Terminal Header */}
      <div className="bg-gray-900 border-b border-gray-700 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-sm font-mono">Terminal Console</span>
          <span className="text-gray-500 text-xs">SSH: {sshConfig.host}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <span className="text-gray-500 text-xs">
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
          <div className="flex items-center gap-1 ml-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
        </div>
      </div>

      {/* Terminal Container */}
      <div 
        ref={terminalRef}
        className="flex-1 p-2"
        style={{ backgroundColor: '#000000', minHeight: '200px' }}
      >
        {!terminal && <div className="text-white">Loading terminal...</div>}
      </div>
    </div>
  );
};

export default XTermDebugConsole;